import { useState, useCallback } from "react";
import {
  ApiDataResponse,
  ApiResponse,
  GradeCriteria,
} from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import { useApiResponseHandler } from "../useApiResponseHandler";

export const useGradeCriteriaService = () => {
  const { setGradeCriterias } = useSchoolsContext();
  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  const createGradeCriteria = useCallback(
    async (
      gradeCriteria: GradeCriteria,
      onSuccess?: (gradeCriteria: GradeCriteria) => void
    ): Promise<{ result: ApiDataResponse<GradeCriteria> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<GradeCriteria>(
          "/gradeCriterias",
          axiosClient
        ).create(gradeCriteria)) as ApiDataResponse<GradeCriteria>;

        if (result.succeeded && result.data) {
          const createdGradeCriteria = result.data;
          setGradeCriterias((prev: GradeCriteria[]) =>
            [...prev, createdGradeCriteria].sort((a, b) => a.order - b.order)
          );
          handleApiSuccess("Grade criteria created successfully");
          onSuccess?.(createdGradeCriteria);
          return { result };
        } else {
          handleApiError(result, "Failed to create grade criteria");
          return { result: { ...result, data: null as any } };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to create grade criteria"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to create grade criteria",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setGradeCriterias]
  );

  const updateGradeCriteria = useCallback(
    async (
      id: string,
      gradeCriteria: GradeCriteria,
      onSuccess?: (gradeCriteria: GradeCriteria) => void
    ): Promise<{ result: ApiDataResponse<GradeCriteria> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<GradeCriteria>(
          "/gradeCriterias",
          axiosClient
        ).update(id, gradeCriteria)) as ApiDataResponse<GradeCriteria>;

        if (result.succeeded) {
          const updatedGradeCriteria = gradeCriteria;
          setGradeCriterias((prev: GradeCriteria[]) =>
            prev.map((g) => (g.id === id ? updatedGradeCriteria : g))
          );
          handleApiSuccess("Grade criteria updated successfully");
          onSuccess?.(updatedGradeCriteria);
          return { result };
        } else {
          handleApiError(result, "Failed to update grade criteria");
          return { result: { ...result, data: null as any } };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to update grade criteria"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update grade criteria",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setGradeCriterias]
  );

  const deleteGradeCriteria = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<GradeCriteria>(
          "/gradeCriterias",
          axiosClient
        ).delete(id);
        if (result.succeeded) {
          setGradeCriterias((prev: GradeCriteria[]) =>
            prev.filter((g) => g.id !== id)
          );
          handleApiSuccess("Grade criteria deleted successfully");
          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete grade criteria");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to delete grade criteria"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to delete grade criteria",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
        setOpenDeleteDialog(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setGradeCriterias]
  );

  return {
    createGradeCriteria,
    updateGradeCriteria,
    deleteGradeCriteria,
    isLoading,
    errors,
    clearErrors,
    openDeleteDialog,
    setOpenDeleteDialog,
  };
};
